import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** className helper that resolves Tailwind class conflicts (later wins). */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format a number as compact currency (USD). 1234567 -> "$1.23M". */
export function formatCompactUSD(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(n);
}

/** Format a number as full-precision USD. */
export function formatUSD(n: number, decimals = 2): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

/** Format a percent (input 0.123 → "+12.30%"). */
export function formatPct(n: number, decimals = 2): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${(n * 100).toFixed(decimals)}%`;
}

/** Format a compact number. 1234567 -> "1.23M". */
export function formatCompact(n: number): string {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(n);
}

/** Promise-based delay used to simulate network latency in mock API calls. */
export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Deterministic pseudo-random in [0,1) seeded by a string — used so mock data
 *  is stable across re-renders without storing every fixture by hand. */
export function seededRand(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Convert to [0, 1)
  return ((h >>> 0) % 1_000_000) / 1_000_000;
}
